/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */

import { IM, useLanguage, useTheme } from '@infominds/react-native-components'
import { useAuthentication } from '@infominds/react-native-license'
import { useNavigation } from '@react-navigation/native'
import Moment from 'moment'
import React, { useEffect, useMemo, useState } from 'react'
import { Platform, ScrollView, StyleSheet } from 'react-native'
import { Col, Grid, Row } from 'react-native-easy-grid'
import Spinner from 'react-native-modal-spinner'

import api from '../apis/Api'
import Colors from '../constants/Colors'
import Layout from '../constants/Layout'
import useDisableBottomNavigation from '../hooks/useDisableBottomNavigation'
import { Bill, HomeScreenNavigationProp } from '../types'
import { PriceUtils } from '../utils/priceUtils'
import { Comment } from './Comment'
import StateBadge from './StateBadge'
import { Text, TouchableOpacity, View } from './Themed'

interface Props {
  bill: Bill
  onDone: () => void
}

function DetailView({ bill, onDone }: Props) {
  const { colorScheme } = useTheme()
  const { i18n, language } = useLanguage()
  const navigation = useNavigation<HomeScreenNavigationProp>()
  useDisableBottomNavigation()
  const { url, token, erp, company } = useAuthentication()

  const [comment, onChangeText] = useState('')
  const [loading, setLoading] = useState(false)

  const [detail, setDetail] = useState<any>()

  const basePrice = useMemo(() => `${i18n.t('BASEPRICE')}: ${PriceUtils.formatPrice(bill.vatBasisAmount ?? 0, language, bill.currency)}`, [bill])
  const vatPrice = useMemo(() => `${i18n.t('VAT')}: ${PriceUtils.formatPrice(bill.vatAmount ?? 0, language, bill.currency)}`, [bill])
  const totalPrice = useMemo(() => `${i18n.t('TOTAL')}: ${PriceUtils.formatPrice(bill.billAmount ?? 0, language, bill.currency)}`, [bill])

  useEffect(() => {
    api
      .getBillDetail(token, url, company, bill.billID, bill.supplierID, bill.billDate)
      .then(res => {
        setDetail(res)
      })
      .catch(console.error)
  }, [bill, token, url, company])

  function format(amount: number, currency: string, id: number, description: string): string {
    let result = amount > 0 ? PriceUtils.formatPrice(amount, language, currency) : ''

    if (id > 0) {
      if (result !== '') result += ' - ' + id
      else result += id
    }

    if (description !== '') {
      if (result !== '') result += ' - ' + description
      else result += description
    }

    return result
  }

  const approve = (accept: boolean) => {
    setLoading(true)
    api
      .approve(token, url, company, bill.msgId, bill.billID, bill.supplierID, bill.billDate, accept, comment)
      .then(() => {
        if (Layout.isSmallDevice) {
          navigation.popToTop()
        } else {
          onDone()
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }

  const returnBill = () => {
    setLoading(true)
    api
      .billReturn(token, url, company, bill.msgId, bill.billID, bill.supplierID, bill.billDate, comment, i18n.t('NORETURN'))
      .then(() => {
        if (Layout.isSmallDevice) {
          navigation.popToTop()
        } else {
          onDone()
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }

  return (
    <ScrollView
      style={{ backgroundColor: Colors[colorScheme].background }}
      keyboardShouldPersistTaps={Platform.OS === 'android' ? 'always' : 'handled'}>
      <View style={styles.container} lightColor={Colors.light.background} darkColor={Colors.dark.background}>
        <Spinner color={Colors.tint} size="large" backgroundColor="transparent" visible={loading} />

        <View>
          <Text style={styles.header}>{bill.supplierName}</Text>
          <View style={styles.detail}>
            <Text>
              {i18n.t('ID_BILLNR')} {bill.billID} {i18n.t('ID_FROM')} {Moment(bill.billDate).format('DD/MM/yyyy')}
            </Text>
            <Text>{basePrice}</Text>
            <Text>{vatPrice}</Text>
            <Text>{totalPrice}</Text>
            {bill.note && (
              <View>
                <Text style={styles.textBold}>
                  {'\n'}
                  {i18n.t('NOTE_EMPLOYEE')}
                </Text>
                <Text>{bill.note}</Text>
              </View>
            )}
            {bill.documentReleaseNote && (
              <View>
                <Text style={styles.textBold}>
                  {'\n'}
                  {i18n.t('NOTE_DOCUMENT')}
                </Text>
                <Text>{bill.documentReleaseNote}</Text>
              </View>
            )}

            <Text style={styles.textBold}>
              {'\n'}
              {i18n.t('DEADLINE')}:
            </Text>
            {/* @ts-ignore todo */}
            {bill.dueDate?.map((dd: any, idx: number) => {
              return (
                <Text key={idx}>
                  {/* @ts-ignore todo */}
                  {PriceUtils.formatPrice(dd.amount ?? 0, language, bill.currency)} - {Moment(dd.date).format('DD/MM/yyyy')}
                </Text>
              )
            })}

            {detail ? (
              <View>
                {detail.accounting.length > 0 && (
                  <Text style={styles.textBold}>
                    {'\n'}
                    {i18n.t('ACCOUNT')}:
                  </Text>
                )}
                {detail.accounting.map((item: any, idx: number) => (
                  // @ts-ignore todo
                  <Text key={idx}>{format(item.amount, bill.currency, item.id, item.description)}</Text>
                ))}

                {detail.cdC.length > 0 && (
                  <Text style={styles.textBold}>
                    {'\n'}
                    {i18n.t('CDC')}:
                  </Text>
                )}
                {detail.cdC.map((item: any, idx: number) => (
                  // @ts-ignore todo
                  <Text key={idx}>{format(item.amount, bill.currency, item.id, item.description)}</Text>
                ))}

                {detail?.objects?.length > 0 && (
                  <Text style={styles.textBold}>
                    {'\n'}
                    {i18n.t('OBJECT')}:
                  </Text>
                )}
                {detail?.objects?.map((item: any, idx: number) => (
                  // @ts-ignore todo
                  <Text key={idx}>{format(item.amount, bill.currency, item.id, item.description)}</Text>
                ))}
                {detail?.projects?.length > 0 && (
                  <Text style={styles.textBold}>
                    {'\n'}
                    {i18n.t('PROJECTS')}:
                  </Text>
                )}
                {detail?.projects?.map((item: any, idx: number) => (
                  // @ts-ignore todo
                  <Text key={idx}>{format(item.amount, bill.currency, item.id, item.description)}</Text>
                ))}
              </View>
            ) : (
              <></>
            )}
          </View>
        </View>
        <View style={styles.previewAndStateRow}>
          <Grid>
            <Col size={50}>
              <TouchableOpacity
                lightColor={Colors.light.listItemBackground}
                darkColor={Colors.dark.listItemBackground}
                style={[styles.button, styles.textButton]}
                // @ts-ignore TODO
                onPress={() => navigation.navigate('PreviewScreen', { bill: bill })}>
                <Text>{i18n.t('PREVIEW')}</Text>
              </TouchableOpacity>
            </Col>
            {/* eslint-disable-next-line react-native/no-inline-styles */}
            <Col size={50} style={{ alignItems: 'flex-end' }}>
              <StateBadge
                state={bill.billState}
                onPress={() => navigation.navigate('HistoryScreen', { bill: bill })}
                isOthers={true}
                // @ts-ignore todo
                hasComment={bill.msgCount > 0}
              />
            </Col>
          </Grid>
        </View>
        <View style={styles.comment}>
          <Comment
            onChangeText={(text: string) => onChangeText(text)}
            value={comment}
            multiline
            // eslint-disable-next-line react-native/no-inline-styles
            style={{ color: Colors[colorScheme].text, borderColor: Colors[colorScheme].listItemDetail, textAlignVertical: 'top' }}
            lightColor={Colors.light.listItemBackground}
            darkColor={Colors.dark.listItemBackground}
          />
        </View>
        <View style={styles.grid}>
          <Grid>
            <Row>
              <Col style={{ marginRight: 10 }}>
                <TouchableOpacity style={[styles.button, styles.primaryButton]} onPress={() => approve(true)}>
                  <IM.Icon size={28} icon={['far', 'thumbs-up']} color="#FFFFFF" />
                </TouchableOpacity>
              </Col>
              <Col>
                <TouchableOpacity style={[styles.button, styles.secondaryButton]} onPress={() => approve(false)}>
                  <IM.Icon size={28} icon={['far', 'thumbs-down']} color="#FFFFFF" />
                </TouchableOpacity>
              </Col>
            </Row>
            <Row>
              {erp !== 'Radix' ? (
                // eslint-disable-next-line react-native/no-inline-styles
                <Col style={{ marginRight: 10 }}>
                  <TouchableOpacity
                    style={[styles.button, styles.textButton]}
                    lightColor={Colors.light.listItemBackground}
                    darkColor={Colors.dark.listItemBackground}
                    onPress={() => returnBill()}>
                    <Text>{i18n.t('ID_RETURN')}</Text>
                  </TouchableOpacity>
                </Col>
              ) : (
                <></>
              )}
              <Col>
                <TouchableOpacity
                  style={[styles.button, styles.textButton]}
                  lightColor={Colors.light.listItemBackground}
                  darkColor={Colors.dark.listItemBackground}
                  onPress={() => {
                    // @ts-ignore todo
                    bill.comment = comment
                    navigation.navigate('DelegateScreen', { bill: bill })
                  }}>
                  <Text>{i18n.t('DELEGATE')}</Text>
                </TouchableOpacity>
              </Col>
            </Row>
          </Grid>
        </View>
      </View>
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  textBold: {
    fontWeight: 'bold',
  },
  container: {
    flex: 1,
    padding: 20,
  },

  header: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  detail: {
    marginBottom: 20,
  },
  previewAndStateRow: {
    width: '100%',
    height: 50,
  },
  grid: {
    width: '100%',
    height: 100,
  },
  comment: {
    width: '100%',
    height: 100,
    marginTop: 10,
    marginBottom: 10,
  },
  button: {
    marginBottom: 10,
    alignItems: 'center',
    borderRadius: 5,
    padding: 5,
  },
  textButton: {
    padding: 10,
  },
  primaryButton: {
    backgroundColor: Colors.tint,
  },
  secondaryButton: {
    backgroundColor: Colors.tintOpposite,
  },
})

export default DetailView
